import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FieldLabel, createUsePuck } from '@puckeditor/core';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { Button } from 'reactstrap';

import { APIgetPage } from '../../../api/Pages';

import { showModalTop, showModal } from '../../../actions';

import {
  usePageId,
  usePageBasePath,
  usePageRouteId,
  usePageVisibilityMode,
} from '../../../helpers/PageIdContext';
import {
  getFileDestination,
  getCollectionDestination,
  getFilePlaceholderElement,
  normalizePageVisibilityMode,
} from '../../../helpers/PageSections';
import { renderPageFileAction } from '../../../helpers/PageElementActions';
import { thumbnailPlaceholder } from '../../../helpers/Files';
import { fileObject } from '../../../helpers/Uploader';
import { isValidUrl } from '../../../helpers/Validators';

import formatIntl from './Common/formatIntl';
import RadioField from './Common/RadioField';
import { normalizeHydratedPageFile } from './Common/pageFileHydration';
import { getPageFileByUuid } from './Common/pageFiles';
import {
  ButtonRadioField,
  ConnectedTargetCollectionField,
  TargetUrlField,
  TargetPageField,
  collectionShape,
  pageShape,
} from './PuckButton/ButtonFields';
import {
  FeedGrid,
  FeedTile,
} from './PuckCollectionFeed/PuckCollectionFeedViews';
import { withLayout } from './PuckLayout';

const DEFAULT_THUMB_WIDTH = 900;
const DEFAULT_THUMB_HEIGHT = 600;
const pageSlugByIdCache = new Map();

const usePuckSelector = createUsePuck();

const hasText = (value) => typeof value === 'string' && value.length > 0;

const fileShape = PropTypes.shape({
  name: PropTypes.string,
  display_name: PropTypes.string,
  original_name: PropTypes.string,
  path: PropTypes.string,
  previewUrl: PropTypes.string,
  thumbnailUrl: PropTypes.string,
  urls: PropTypes.shape({
    preview: PropTypes.string,
    thumbnail: PropTypes.string,
  }),
  uuid: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
});

const buildNormalizedUrls = (rawFile) => {
  const sourceUrls = (rawFile && rawFile.urls && typeof rawFile.urls === 'object')
    ? rawFile.urls
    : {};
  const preferredThumbnail = sourceUrls.thumbnail
    || sourceUrls.thumbnailsq
    || sourceUrls.thumbnailmasonry
    || sourceUrls.thumbnailx2
    || sourceUrls.preview
    || sourceUrls.original
    || rawFile?.thumbnailUrl
    || rawFile?.previewUrl
    || rawFile?.url
    || thumbnailPlaceholder;
  const hasApiThumbnail = hasText(sourceUrls.thumbnail) || hasText(sourceUrls.preview);

  if (hasApiThumbnail) {
    const thumbnail = sourceUrls.thumbnail || sourceUrls.preview || preferredThumbnail;

    return {
      ...sourceUrls,
      preview: sourceUrls.preview
        || sourceUrls.thumbnail
        || sourceUrls.original
        || preferredThumbnail,
      thumbnail,
      thumbnailx2: sourceUrls.thumbnailx2 || thumbnail,
      thumbnailsq: sourceUrls.thumbnailsq || thumbnail,
      thumbnailmasonry: sourceUrls.thumbnailmasonry || thumbnail,
    };
  }

  const baseThumbnail = preferredThumbnail;

  return {
    ...sourceUrls,
    preview: sourceUrls.preview
      || sourceUrls.original
      || rawFile?.previewUrl
      || rawFile?.url
      || baseThumbnail,
    thumbnail: sourceUrls.thumbnail || baseThumbnail,
    thumbnailx2: sourceUrls.thumbnailx2 || sourceUrls.thumbnail || baseThumbnail,
    thumbnailsq: sourceUrls.thumbnailsq || sourceUrls.thumbnail || baseThumbnail,
    thumbnailmasonry: sourceUrls.thumbnailmasonry || sourceUrls.thumbnail || baseThumbnail,
  };
};

const normalizeFileForFeedViews = (rawFile) => {
  if (!rawFile) {
    return null;
  }

  const normalizedUrls = buildNormalizedUrls(rawFile);

  const width = rawFile?.attributes?.thumbnail_width
    || rawFile?.attributes?.width
    || rawFile?.width
    || DEFAULT_THUMB_WIDTH;
  const height = rawFile?.attributes?.thumbnail_height
    || rawFile?.attributes?.height
    || rawFile?.height
    || DEFAULT_THUMB_HEIGHT;

  return {
    ...fileObject,
    ...rawFile,
    id: rawFile?.id,
    uuid: rawFile?.uuid,
    name: rawFile?.name,
    display_name: rawFile?.display_name,
    original_name: rawFile?.original_name,
    type: rawFile?.type,
    path: rawFile?.path,
    attributes: rawFile?.attributes || {
      width,
      height,
      thumbnail_width: width,
      thumbnail_height: height,
    },
    urls: normalizedUrls,
  };
};

const getPickerThumbnailUrl = (fileData) => (
  fileData?.thumbnailUrl
  || fileData?.urls?.thumbnailmasonry
  || fileData?.urls?.thumbnailsq
  || fileData?.urls?.thumbnail
  || fileData?.urls?.thumbnailx2
  || fileData?.urls?.preview
  || fileData?.urls?.original
  || fileData?.previewUrl
  || fileData?.url
  || null
);

const getPickerDisplayName = (fileData) => (
  fileData?.display_name || fileData?.name || fileData?.original_name || ''
);

const toPagePath = (pageRouteToken, pageBasePath = 'pages') => {
  if (pageRouteToken == null || pageRouteToken === '') return '';

  const routeToken = String(pageRouteToken);
  if (isValidUrl(routeToken)) return routeToken;

  if (routeToken.startsWith('/')) {
    return routeToken;
  }

  return `/${pageBasePath}/${routeToken}`;
};

const resolveDestination = ({
  targetType,
  displayFile,
  targetCollection,
  targetPage,
  url,
  pageVisibilityMode,
  pageBasePath,
  pageRouteId,
  pageId,
  backUrl,
}) => {
  if (targetType === 'collection' && targetCollection?.uuid) {
    if (pageRouteId) {
      return `/${pageBasePath}/${pageRouteId}/collection/${targetCollection.uuid}`;
    }

    return getCollectionDestination({
      collection: targetCollection,
      pageVisibilityMode,
      pageBasePath,
      pageRouteId,
    });
  }

  if (targetType === 'file' && displayFile?.uuid) {
    return getFileDestination({
      file: displayFile,
      pageVisibilityMode,
      pageBasePath,
      pageRouteId,
      pageId,
      backUrl,
    });
  }

  if (targetType === 'page' && (targetPage?.url || targetPage?.id)) {
    return toPagePath(targetPage?.url || targetPage?.id, pageBasePath);
  }

  if (targetType === 'url' && url) {
    return url;
  }

  return '';
};

const normalizePickerValue = (fileData) => {
  if (!fileData) {
    return null;
  }

  return {
    ...fileData,
    thumbnailUrl: getPickerThumbnailUrl(fileData),
    name: getPickerDisplayName(fileData),
  };
};

const FilePickerField = ({ value, onChange, onShowModalTop }) => {
  const intl = useIntl();
  const pageId = usePageId();
  const [displayValue, setDisplayValue] = useState(value || null);

  useEffect(() => {
    let isMounted = true;
    const cleanup = () => {
      isMounted = false;
    };

    if (!value?.uuid) {
      setDisplayValue(normalizePickerValue(value));
      return cleanup;
    }

    const hydratedValue = normalizePickerValue(value);
    const thumbnailUrl = hydratedValue?.thumbnailUrl;

    if (thumbnailUrl || !pageId) {
      setDisplayValue(hydratedValue);
      return cleanup;
    }

    getPageFileByUuid({ pageId, fileUuid: value.uuid, fallbackFile: value })
      .then((resolved) => {
        if (!isMounted) {
          return;
        }

        const resolvedValue = normalizePickerValue(
          normalizeHydratedPageFile(value, resolved || value),
        );

        setDisplayValue({
          ...hydratedValue,
          ...resolvedValue,
        });
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        setDisplayValue(hydratedValue);
      });

    return cleanup;
  }, [value, pageId]);

  const openModal = () => {
    onShowModalTop('FileSelectModalEnhanced', {
      className: 'modal-select-file modal-select-file-enhanced',
      allowUpload: false,
      handleSelectFile: () => {},
      handleFileData: (fileData) => {
        if (!fileData) return;
        onChange({
          id: fileData.id,
          path: fileData.path,
          name: fileData.name,
          display_name: fileData.display_name,
          original_name: fileData.original_name,
          type: fileData.type,
          urls: fileData.urls,
          attributes: fileData.attributes,
          url: fileData.urls?.original || fileData.urls?.preview || fileData.url || null,
          allow_download: fileData.allow_download,
          previewUrl: fileData.urls?.preview || fileData.urls?.original || fileData.url || null,
          thumbnailUrl: getPickerThumbnailUrl(fileData),
          uuid: fileData.uuid,
          width: fileData.width,
          height: fileData.height,
        });
      },
    });
  };

  const handleRemove = () => onChange(null);

  return (
    <div className="puck-file-field-label">
      <FieldLabel label={intl.formatMessage({ defaultMessage: 'File' })}>
        {displayValue?.thumbnailUrl && (
          <div className="puck-file-preview">
            <img
              src={displayValue.thumbnailUrl}
              alt={intl.formatMessage({ defaultMessage: 'Selected file' })}
              className="puck-file-preview-img"
            />
          </div>
        )}
        <div className="puck-file-picker-buttons">
          <Button size="sm" color="primary" onClick={openModal} className="w-100 mb-1">
            {displayValue
              ? intl.formatMessage({ defaultMessage: 'Change file' })
              : intl.formatMessage({ defaultMessage: 'Select file' })}
          </Button>
          {displayValue && (
            <Button size="sm" color="secondary" onClick={handleRemove} className="w-100">
              {intl.formatMessage({ defaultMessage: 'Remove' })}
            </Button>
          )}
        </div>
      </FieldLabel>
    </div>
  );
};

FilePickerField.propTypes = {
  value: fileShape,
  onChange: PropTypes.func.isRequired,
  onShowModalTop: PropTypes.func.isRequired,
};

FilePickerField.defaultProps = {
  value: null,
};

const AllowFileDownloadField = ({ value, onChange, field }) => {
  const visibilityMode = usePuckSelector((s) => s.appState?.data?.root?.props?.visibility_mode);
  const isHomepageVisibilityMode = normalizePageVisibilityMode(visibilityMode) === 'homepage';

  useEffect(() => {
    if (isHomepageVisibilityMode && value !== false) {
      onChange(false);
    }
  }, [isHomepageVisibilityMode, onChange, value]);

  if (isHomepageVisibilityMode) {
    return <div className="puck-download-setting-hidden" />;
  }

  return (
    <RadioField
      id="puck-file-download"
      name="puckFileDownload"
      label={field.label}
      value={value}
      onChange={onChange}
      options={field.options}
    />
  );
};

AllowFileDownloadField.propTypes = {
  value: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  field: PropTypes.shape({
    label: PropTypes.string,
    options: PropTypes.array,
  }).isRequired,
};

AllowFileDownloadField.defaultProps = {
  value: true,
};

const PuckFileRender = ({
  file,
  allowDownload,
  view,
  targetType,
  targetCollection,
  targetPage,
  url,
  page_visibility_mode: pageVisibilityMode,
  puck,
  onShowModal,
}) => {
  const intl = useIntl();
  const contextVisibilityMode = usePageVisibilityMode();
  const pageId = usePageId();
  const pageRouteId = usePageRouteId();
  const pageBasePath = usePageBasePath();
  const location = useLocation();
  const isEditing = !!puck?.isEditing;
  const [resolvedFile, setResolvedFile] = useState(null);
  const [resolvedTargetPage, setResolvedTargetPage] = useState(targetPage || null);
  const fileUuid = file?.uuid;
  const effectivePageVisibilityMode = normalizePageVisibilityMode(
    pageVisibilityMode || contextVisibilityMode,
  );

  useEffect(() => {
    let isMounted = true;
    const fallbackFile = file || null;

    if (!fileUuid) {
      setResolvedFile(fallbackFile);
      return () => {
        isMounted = false;
      };
    }

    if (!pageId) {
      setResolvedFile(fallbackFile);
      return () => {
        isMounted = false;
      };
    }

    getPageFileByUuid({ pageId, fileUuid, fallbackFile })
      .then((resolved) => {
        if (!isMounted) {
          return;
        }

        setResolvedFile(normalizeHydratedPageFile(fallbackFile, resolved || fallbackFile));
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        setResolvedFile(fallbackFile);
      });

    return () => {
      isMounted = false;
    };
  }, [pageId, fileUuid, file]);

  useEffect(() => {
    let isMounted = true;

    if (targetType !== 'page' || !targetPage) {
      setResolvedTargetPage(targetPage || null);
      return () => {
        isMounted = false;
      };
    }

    const targetPageId = targetPage.id;
    if (!targetPageId) {
      setResolvedTargetPage(targetPage);
      return () => {
        isMounted = false;
      };
    }

    const cacheKey = String(targetPageId);
    const cachedSlug = pageSlugByIdCache.get(cacheKey);

    if (cachedSlug) {
      setResolvedTargetPage({
        ...targetPage,
        url: cachedSlug,
      });
      return () => {
        isMounted = false;
      };
    }

    APIgetPage(targetPageId, isEditing ? { edit_mode: true } : {})
      .then((response) => {
        if (!isMounted) {
          return;
        }

        const currentSlug = response?.data?.url;
        if (currentSlug) {
          pageSlugByIdCache.set(cacheKey, currentSlug);
        }

        setResolvedTargetPage({
          ...targetPage,
          url: currentSlug || targetPage.url,
        });
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        setResolvedTargetPage(targetPage);
      });

    return () => {
      isMounted = false;
    };
  }, [targetType, targetPage, isEditing]);

  const displayFile = resolvedFile || file;

  if (!displayFile?.previewUrl && !displayFile?.uuid) {
    return (
      <div className="puck-file puck-file--empty">
        {getFilePlaceholderElement({ className: 'puck-file-preview' })}
        {isEditing && (
          <div className="center puck-component-info-text">
            {intl.formatMessage({ defaultMessage: 'No file selected' })}
          </div>
        )}
      </div>
    );
  }

  const allowDownloadResolved = allowDownload == null
    ? !!resolvedFile?.allow_download
    : !!allowDownload;
  const viewMode = view || 'grid';
  const resolvedTargetType = targetType || 'file';
  const backUrl = `${location.pathname}${location.search || ''}`;

  const fileForViews = normalizeFileForFeedViews(displayFile);
  const destination = resolveDestination({
    targetType: resolvedTargetType,
    displayFile: fileForViews,
    targetCollection,
    targetPage: resolvedTargetType === 'page' ? resolvedTargetPage : targetPage,
    url,
    pageVisibilityMode: effectivePageVisibilityMode,
    pageBasePath,
    pageRouteId,
    pageId,
    backUrl,
  });

  const fileForLink = (resolvedTargetType === 'url' && isValidUrl(destination))
    ? {
      ...fileForViews,
      type: 'link',
      path: destination,
      to: null,
      ...(resolvedTargetType === 'none') && { pointToNone: true },
      ...(resolvedTargetType === 'url') && { hideMeta: true },
    }
    : {
      ...fileForViews,
      to: destination || null,
      ...(resolvedTargetType === 'none') && { pointToNone: true },
      ...(resolvedTargetType === 'url') && { hideMeta: true },
    };

  const files = [
    fileForLink,
  ];

  const renderDisplayAction = (actionFile, actionView) => renderPageFileAction({
    actionFile,
    actionView,
    displayFile,
    allowDownload: allowDownloadResolved,
    pageId,
    onShowModal,
    intl,
  });

  return (
    <div className="puck-file">
      <div className="puck-file-media">
        {viewMode === 'grid' && (
          <div className="puck-file-grid-host">
            <FeedGrid
              files={files}
              columns={3}
              renderDisplayAction={renderDisplayAction}
            />
          </div>
        )}
        {viewMode === 'framed' && (
          <div className="puck-file-grid-host">
            <FeedGrid
              files={files}
              columns={3}
              renderDisplayAction={renderDisplayAction}
              selectedView="framed"
            />
          </div>
        )}
        {viewMode === 'tile' && (
          <div className="puck-file-tile-host">
            <FeedTile
              files={files}
              renderDisplayAction={renderDisplayAction}
              overwriteUrl={(tileFile) => tileFile?.url || null}
            />
          </div>
        )}
      </div>
    </div>
  );
};

PuckFileRender.propTypes = {
  file: fileShape,
  allowDownload: PropTypes.bool,
  view: PropTypes.oneOf(['grid', 'framed', 'tile', 'masonry', 'radio']),
  targetType: PropTypes.oneOf(['collection', 'file', 'url', 'page']),
  targetCollection: collectionShape,
  targetPage: pageShape,
  url: PropTypes.string,
  page_visibility_mode: PropTypes.string,
  puck: PropTypes.object,
  onShowModal: PropTypes.func.isRequired,
};

PuckFileRender.defaultProps = {
  file: null,
  allowDownload: true,
  view: 'grid',
  targetType: 'file',
  targetCollection: null,
  targetPage: null,
  url: '',
  page_visibility_mode: null,
  puck: null,
};

const PuckFileConfig = {
  fields: {
    file: {
      type: 'custom',
      get label() { return formatIntl('File'); },
      render: (props) => <ConnectedFilePickerField {...props} />,
    },
    allowDownload: {
      type: 'custom',
      get label() { return formatIntl('File download'); },
      options: [
        { get label() { return formatIntl('Enable'); }, value: true },
        { get label() { return formatIntl('Disable'); }, value: false },
      ],
      render: (props) => <AllowFileDownloadField {...props} />,
    },
    view: {
      type: 'radio',
      get label() { return formatIntl('Display type'); },
      options: [
        { get label() { return formatIntl('Grid'); }, value: 'grid' },
        { get label() { return formatIntl('Framed'); }, value: 'framed' },
        { get label() { return formatIntl('Tile'); }, value: 'tile' },
      ],
    },
    targetType: {
      type: 'custom',
      get label() { return formatIntl('Point to'); },
      options: [
        { get label() { return formatIntl('This file'); }, value: 'file' },
        { get label() { return formatIntl('Collection'); }, value: 'collection' },
        { get label() { return formatIntl('URL'); }, value: 'url' },
        { get label() { return formatIntl('Page'); }, value: 'page' },
        { get label() { return formatIntl('None'); }, value: 'none' },
      ],
      render: (props) => <ButtonRadioField {...props} value={props.value || 'file'} />,
    },
    targetCollection: {
      type: 'custom',
      get label() { return formatIntl('Collection'); },
      render: (props) => <ConnectedTargetCollectionField {...props} />,
    },
    url: {
      type: 'custom',
      get label() { return formatIntl('URL'); },
      render: (props) => <TargetUrlField {...props} />,
    },
    targetPage: {
      type: 'custom',
      get label() { return formatIntl('Page'); },
      render: (props) => <TargetPageField {...props} />,
    },
  },

  defaultProps: {
    file: null,
    allowDownload: true,
    view: 'grid',
    targetType: 'file',
    targetCollection: null,
    targetPage: null,
    url: '',
  },

  render: (props) => <ConnectedPuckFileRender {...props} />,
};

const mapDispatchToProps = (dispatch) => ({
  onShowModal: (modalName, params) => dispatch(showModal(modalName, params)),
  onShowModalTop: (modalName, params) => dispatch(showModalTop(modalName, params)),
});

const ConnectedFilePickerField = connect(null, mapDispatchToProps)(FilePickerField);
const ConnectedPuckFileRender = connect(null, mapDispatchToProps)(PuckFileRender);

export default withLayout(PuckFileConfig);
